import { useEffect, useRef, useState } from "react";
import { BookMarked } from "lucide-react";
import { api } from "@/lib/api";
import { formatCurrency } from "@/lib/format";

// Item-description input with "price memory" autocomplete: matches past buy
// costs from the private price book. Dropdown uses fixed positioning so it is
// never clipped by the table's overflow container.
export const PriceBookSuggest = ({ value, onChange, onPick, testId }) => {
  const [hits, setHits] = useState([]);
  const [rect, setRect] = useState(null);
  const wrap = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (wrap.current && !wrap.current.contains(e.target)) setRect(null);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const query = (text) => {
    clearTimeout(timer.current);
    if ((text || "").trim().length < 2) { setHits([]); setRect(null); return; }
    timer.current = setTimeout(async () => {
      try {
        const res = await api.priceBook(text);
        setHits(res);
        if (res.length && wrap.current) {
          setRect(wrap.current.getBoundingClientRect());
        } else {
          setRect(null);
        }
      } catch { /* ignore */ }
    }, 250);
  };

  return (
    <div ref={wrap} className="relative">
      <input
        data-testid={testId}
        className="input"
        value={value}
        autoComplete="off"
        onChange={(e) => { onChange(e.target.value); query(e.target.value); }}
      />
      {rect && hits.length > 0 && (
        <div
          data-testid="price-book-dropdown"
          className="fixed z-50 bg-white border border-slate-200 rounded-lg shadow-xl max-h-56 overflow-y-auto"
          style={{ top: rect.bottom + 4, left: rect.left, width: Math.max(rect.width, 320) }}
        >
          <div className="px-2.5 py-1 text-[10px] uppercase tracking-wider text-amber-700 bg-amber-50 flex items-center gap-1 sticky top-0">
            <BookMarked size={10} /> Price memory — internal only
          </div>
          {hits.map((h) => (
            <button
              key={h.id}
              type="button"
              data-testid={`pb-hit-${h.id}`}
              className="w-full text-left px-2.5 py-1.5 hover:bg-emerald-50/60 border-t border-slate-50"
              onMouseDown={(e) => { e.preventDefault(); onPick(h); setRect(null); }}
            >
              <div className="text-xs font-medium text-slate-800 truncate">{h.description}</div>
              <div className="text-[10px] text-slate-500">
                Cost {formatCurrency(h.cost_price, h.currency)} · Sale {formatCurrency(h.unit_price, h.currency)}
                {h.margin_pct ? ` (${h.margin_pct}%)` : ""} · used {h.times_used || 1}×
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
